import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Box, Button } from '@rsos/components/capstone/base';
import { _911INFORM } from '@rsos/constants/spatialLayers';
import {
  gspx,
  GPX_EVENT_TYPES,
  GPX_FEATURES,
  GPX_CAPABILITIES,
  GPX_ROUTES,
} from '@rsos/gainsight';
import checkEsriMapAccess from '@rsos/utils/checkEsriMapAccess';
import { captureException } from '@rsos/utils/sentry';
import usePortalType from '@rsos/utils/usePortalType';
import createDeepLink from '../../../../../actions/createDeepLink';
import LocationSummary, {
  StyledLocationSummary,
} from '../../../../Map/MapCard/LocationSummary';
import _911informConfirmationModal from './CallOutToTabConfirmationModal';

export const STATUS_ACTIVE = 'active';
export const STATUS_INACTIVE = 'inactive';

const LocationContainer = styled(Box)`
  ${StyledLocationSummary} {
    margin-bottom: 0;
  }
`;
const ButtonContainer = styled(Box)`
  border: ${({ isFromMap, theme }) =>
    !isFromMap && `1px solid ${theme.colors.heavyLineOutline};}`}
  margin: ${({ isFromMap }) => !isFromMap && '11px 0'};
  padding: ${({ isFromMap }) => !isFromMap && '13px'};
`;

/**
 * Find and return the spatial object for the 911inform spatial layer based on
 * the `id` (data from the 911 call in the call pane) or the `mapCardData`
 * @param {Integer} id - spatial object ID
 */
const useSpatialObject = id => {
  return useSelector(state => {
    if (id) {
      return state.spatialLayers.staticLayers
        .find(layer => layer.source === _911INFORM)
        ?.spatial_objects.find(object => object.id === id);
    }
    return state.mapCard.mapCardData.spatialObject;
  });
};

const CallOutToTab = props => {
  const [showModal, setShowModal] = useState(false);
  const [requestingDeepLink, setRequestingDeepLink] = useState(false);
  const dispatch = useDispatch();
  const spatialObject = useSpatialObject(props?.data?.spatial_object_id);
  const spatialObjectId = spatialObject?.id;
  const isFromMap = !props?.data?.spatial_object_id; // It's from the map if this data is falsy

  const isPremiumUser = usePortalType().isPremiumUser;
  const hasEsriMapAccess =
    useSelector(state =>
      checkEsriMapAccess({
        psaps: state.psaps,
        sinatra: state.sinatra,
      }),
    ) && isPremiumUser;

  const open911informTab = link => {
    const trackingName = `Click on 911inform link ${
      isFromMap ? 'map' : 'call event pane'
    }`;
    const trackingCategory = `${hasEsriMapAccess ? 'Esri' : 'Google'} Map`;
    gspx.trackEvent({
      name: 'map_click_911inform_link',
      type: GPX_EVENT_TYPES.INFO,
      feature: GPX_FEATURES.LEGACY,
      capability: GPX_CAPABILITIES.LEGACY,
      route: GPX_ROUTES.MAP,
      additionalKeys: {
        legacy_category: 'Data Map',
        legacy_name: trackingName,
        Category: trackingCategory,
        'Launched date': new Date(),
      },
    });

    window.open(link, '_blank', 'noopener noreferrer');
    setRequestingDeepLink(false);
  };

  const handleAccessBuilding = () => {
    const trackingName = `Click on 911inform call-out-to-tab ${
      isFromMap ? 'map' : 'call event pane'
    }`;
    gspx.trackEvent({
      name: 'map_click_911inform_call_out_to_tab',
      type: GPX_EVENT_TYPES.INFO,
      feature: GPX_FEATURES.LEGACY,
      capability: GPX_CAPABILITIES.LEGACY,
      route: GPX_ROUTES.MAP,
      additionalKeys: {
        legacy_category: 'Data Map',
        legacy_name: trackingName,
        Category: 'Spatial Layer',
        'Launched date': new Date(),
      },
    });

    setShowModal(true);
  };

  const handleConfirm = async () => {
    const deepLink = props?.data?.deep_link_url;
    if (spatialObject.status === STATUS_ACTIVE && deepLink) {
      open911informTab(deepLink);
      setShowModal(false);
    } else {
      setRequestingDeepLink(true);
      try {
        const resp = await dispatch(
          createDeepLink(spatialObjectId, _911INFORM),
        );
        if (resp?.url) {
          open911informTab(resp.url);
        } else {
          // TODO: error handling for failed API request
        }
      } catch (error) {
        /* This should be unlikely but report it just in case. */
        captureException(error);
      } finally {
        setRequestingDeepLink(false);
        setShowModal(false);
      }
    }
  };
  const address = spatialObject?.attributes?.address;
  const coordinates = {
    lat: spatialObject?.center?.coordinates[1],
    lng: spatialObject?.center?.coordinates[0],
  };

  return (
    <>
      <LocationContainer
        px="12px"
        isFromMap={isFromMap}
        className="LocationContainer"
      >
        {!isFromMap && (
          <LocationSummary
            location={address}
            coordinates={coordinates}
            overridden
          />
        )}
      </LocationContainer>

      <Box justify="center" px="13px">
        <ButtonContainer isFromMap={isFromMap}>
          <Button onClick={handleAccessBuilding} disabled={requestingDeepLink}>
            {props.schema.componentProperties.properties.actionText}
          </Button>
          <_911informConfirmationModal
            isOpened={showModal}
            onClose={() => {
              setShowModal(false);
            }}
            onConfirm={handleConfirm}
          />
        </ButtonContainer>
      </Box>
    </>
  );
};

export default CallOutToTab;
